import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import {
  getCurrentUserServerOrFail,
  loadRawEntityDataByIDOrFail,
  loadRawEntityDataByIDs,
} from '@adornis/bh-util/util.js';
import { assignRoleToUser, removeRoleFromUser } from '@adornis/users/server/roles.js';
import { switchMap } from 'rxjs';
import { Product } from '../entities/products.js';
import { Teaching } from '../entities/teaching.js';
import { isLecturerInInstitution, User } from '../entities/user.js';
import { Permission, Role } from '../enums/roles.js';
import { notifyLecturerAddedToProduct } from '../notification/lecturer-added-to-product.js';
import { mongo } from '../server/mongo.js';
import { notifyUserPermissionChange } from '../server/user-change-notifier.js';

export const subscribeLecturersByProduct = registerSubscription({
  operationName: 'subscribeLecturersByProduct',
  resolve:
    (productID: string) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) =>
      watchQuery<EntityData<Teaching>>(Teaching._collectionName, { productID }, { projection: { lecturerID: 1 } }).pipe(
        switchMap(async teachings => {
          const lecturers = await loadRawEntityDataByIDs(
            User,
            teachings.map(teaching => teaching.lecturerID),
            { _id: 1, username: 1, firstName: 1, lastName: 1 },
          );
          return lecturers.map(lecturer => new User(lecturer));
        }),
      ),
  type: () => [User],
  params: [{ name: 'productID', type: () => String }],
});

export const getTeachingByLecturerAndProduct = registerQuery({
  operationName: 'getTeachingByLecturerAndProduct',
  resolve:
    (productID: string, lecturerID: string) =>
    async <SelSet extends BaseQLSelectionSet<Teaching>>(gelFields: SelSet) => {
      if (!context.serverContext) {
        const currentUser = await getCurrentUserServerOrFail();
        const product = await loadRawEntityDataByIDOrFail(Product, productID, { institutionID: 1 });
        const isLecturer = currentUser._id === lecturerID;
        const mayViewLecturers = currentUser.hasPermission(
          Permission.VIEW_ADMIN_INSTITUTION_LECTURERS,
          product.institutionID,
        );
        if (!isLecturer && !mayViewLecturers) throw new Error('not authorized');
      }
      const col = mongo().collection<Teaching>(Teaching._collectionName);
      return (await col.find({ productID, lecturerID }).toArray())?.[0] as Maybe<Teaching>;
    },
  type: () => Teaching,
  params: [
    { name: 'productID', type: () => ID },
    { name: 'lecturerID', type: () => ID },
  ],
});

export const switchLecturerRole = registerMutation({
  operationName: 'switchLecturerRole',
  type: () => Int,
  params: [
    { name: 'institutionID', type: () => String }, //
    { name: 'lecturerID', type: () => String }, //
  ],
  resolve: (institutionID: string, lecturerID: string) => async () => {
    const currentUser = await getCurrentUserServerOrFail();
    if (!currentUser.hasPermission(Permission.EDIT_LECTURERS, institutionID))
      throw new Error('You dont have enough permission!');

    const user = await loadRawEntityDataByIDOrFail(User, lecturerID, { roles: { name: 1, contexts: 1 } });
    const isLecturer = isLecturerInInstitution(user.roles, institutionID);

    if (isLecturer) {
      // only this institution: without contexts, removeRoleFromUser drops the lecturer role in all institutions
      await removeRoleFromUser(lecturerID, Role.LECTURER, [institutionID]);
      await assignRoleToUser(lecturerID, Role.REFERENT, [institutionID], User as any);
      notifyUserPermissionChange({ userID: lecturerID, kind: 'roles' });
    } else {
      await removeRoleFromUser(lecturerID, Role.REFERENT, [institutionID]);
      await assignRoleToUser(lecturerID, Role.LECTURER, [institutionID], User as any);
      notifyUserPermissionChange({ userID: lecturerID, kind: 'roles' });

      // Send "added to product" mail for each existing teaching in this institution
      const teachingsCol = mongo().collection<Teaching>(Teaching._collectionName);
      const teachings = await teachingsCol.find({ lecturerID }).toArray();
      const productIDs = Array.from(new Set(teachings.map(t => t.productID).filter(Boolean) as string[]));
      const products =
        productIDs.length > 0 ? await loadRawEntityDataByIDs(Product, productIDs, { _id: 1, institutionID: 1 }) : [];
      const productInstitutionByID = new Map<string, string>(
        products.map(p => [(p as any)._id as string, (p as any).institutionID as string]),
      );
      for (const teaching of teachings) {
        if (productInstitutionByID.get(teaching.productID) !== institutionID) continue;
        await notifyLecturerAddedToProduct(lecturerID, teaching.productID);
      }
    }

    return 1;
  },
});
